import { useRef, useState, useCallback } from "react";

// Draggable bottom-sheet sizing for the mobile Map & Calendar lists.
// The grab handle can be dragged to ANY height (not just fixed snap points); dragging
// very low collapses it to a "peek" bar. The last open height + collapsed state are
// remembered per device (localStorage) so the sheet reopens where the rider left it.
const PEEK = 56;        // collapsed bar height (just the grab handle)
const MIN_OPEN = 140;   // drag below this => collapse to peek

function readStore(key) {
  try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
}

export function useSheetSize(key, { defaultCollapsed = false } = {}) {
  const init = readStore(key);
  const vh = () => (typeof window !== "undefined" ? window.innerHeight : 800);
  const [openH, setOpenH] = useState(() => {
    const v = Number(init.openH);
    return v >= MIN_OPEN ? v : Math.round(vh() * 0.55);
  });
  const [collapsed, setColl] = useState(() =>
    typeof init.collapsed === "boolean" ? init.collapsed : defaultCollapsed
  );
  const drag = useRef(null);

  const persist = useCallback((oh, col) => {
    try { localStorage.setItem(key, JSON.stringify({ openH: oh, collapsed: col })); } catch { /* ignore */ }
  }, [key]);

  const setCollapsed = useCallback((v) => { setColl(v); persist(openH, v); }, [openH, persist]);

  const isPortrait = () => typeof window === "undefined" || window.matchMedia("(orientation: portrait)").matches;
  const maxPx = () => Math.round(vh() * 0.9);

  const onPointerDown = (e) => {
    if (!isPortrait()) return;
    drag.current = { y: e.clientY, startH: collapsed ? PEEK : openH, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onPointerMove = (e) => {
    const d = drag.current; if (!d) return;
    const dy = d.y - e.clientY; // drag up => grow
    if (Math.abs(dy) > 4) d.moved = true;
    const h = Math.max(PEEK, Math.min(maxPx(), d.startH + dy));
    if (h < MIN_OPEN) { setColl(true); }
    else { setColl(false); setOpenH(h); }
  };
  const onPointerUp = () => {
    const d = drag.current; drag.current = null; if (!d) return;
    if (!d.moved) { const nc = !collapsed; setColl(nc); persist(openH, nc); return; } // tap toggles
    persist(openH, collapsed);
  };

  return {
    height: collapsed ? PEEK : openH,
    collapsed,
    setCollapsed,
    grabberProps: { onPointerDown, onPointerMove, onPointerUp, style: { touchAction: "none", cursor: "ns-resize" } },
  };
}
